<template>
  <meta name="viewport" content="width=device-width, initial-scale=0.95">

  <div class="container slide-in-bottom">
    <img class="logo" :src="worker.pfp" :alt="worker.username" />
    <div class="worker-name">{{ worker.username }}</div>
    <div class="worker-role">{{ worker.role }}</div>

    <div v-if="!showConfirmation">
      <FormKit :actions="false" type="form">
        <FormKit outer-class="multi-step-container" tab-style="progress" type="multi-step">
          <FormKit name="Zeitpunkt" type="step">
            <div class="pre-form-container">
              <DateForm :currentWorker="worker" @updateDateTime="handleDateTimeUpdate" />
            </div>
            <template #stepNext="{ handlers }">
              <div class="button-container">
                <FormKit
                    data-next="true"
                    label="Weiter"
                    outer-class="next-button"
                    type="button"
                    :disabled="!isDateTimeSelected"
                    @click="() => handlers.incrementStep(1)()"
                />
              </div>
            </template>
          </FormKit>
          <FormKit name="Ihre Informationen" type="step">
            <div class="pre-form-container">
              <IdentityForm @updateIdentity="handleIdentityUpdate" />
            </div>
            <template #stepNext="{ handlers }">
              <FormKit
                  data-next="true"
                  label="Weiter"
                  outer-class="next-button"
                  type="button"
                  :disabled="!isIdentityInfoComplete"
                  @click="() => handlers.incrementStep(1)()"
              />
            </template>
            <template #stepPrevious="{ handlers }">
              <FormKit
                  type="button"
                  @click="() => handlers.incrementStep(-1)()"
                  label="Zurück"
              />
            </template>
          </FormKit>
          <FormKit name="Typ" type="step">
            <div class="pre-form-container">
              <TypeForm @updateType="handleTypeUpdate" />
            </div>
            <template #stepNext="{ handlers }">
              <FormKit
                  data-next="true"
                  label="Weiter"
                  outer-class="next-button"
                  type="button"
                  :disabled="!isTypeComplete"
                  @click="() => handlers.incrementStep(1)()"
              />
            </template>
            <template #stepPrevious="{ handlers }">
              <FormKit
                  type="button"
                  @click="() => handlers.incrementStep(-1)()"
                  label="Zurück"
              />
            </template>
          </FormKit>
          <FormKit name="Termin vereinbaren" type="step">
            <div class="pre-form-container">
              <SubmitAppointmentForm
                  :identityInfo="identityInfo"
                  :selectedDateTime="selectedDateTime"
                  :worker="worker"
                  :selectedType="selectedType"
              />
            </div>
            <template #stepNext>
              <FormKit
                  label="Senden"
                  outer-class="submit-button-form"
                  type="button"
                  @click="submitAppointment"
              />
            </template>
            <template #stepPrevious="{ handlers }">
              <FormKit
                  type="button"
                  @click="() => handlers.incrementStep(-1)()"
                  label="Zurück"
              />
            </template>
          </FormKit>
        </FormKit>
      </FormKit>
    </div>

    <div v-if="showConfirmation" class="confirmation-message">
      <p>Ihr Termin wurde erfolgreich vereinbart!</p>
      <p>Bitte überprüfen Sie Ihren E-Mail Eingang für weitere Details.</p>
    </div>

    <transition name="slide-fade">
      <div v-if="showStatusMessage" :class="['status-message', success ? 'success' : 'error']">
        <p>{{ statusMessage }}</p>
      </div>
    </transition>
  </div>
  <div v-if="loading" class="loading-overlay">
    <img class="loading-spinner" src="../assets/logo.png" alt="">
  </div>
</template>

<script>
import DateForm from "@/components/DateForm.vue";
import IdentityForm from "@/components/IdentityForm.vue";
import SubmitAppointmentForm from "@/components/SubmitAppointmentForm.vue";
import workerService from "@/services/workerService";
import requestService from "@/services/requestService";
import validator from "email-validator";
import TypeForm from "@/components/TypeForm.vue";
export default {
  name: "RequestAppointmentView",
  components: {
    TypeForm,
    IdentityForm,
    DateForm,
    SubmitAppointmentForm,
  },
  data() {
    return {
      selectedType: null,
      worker: {},
      selectedDateTime: null,
      identityInfo: {
        firstName: "",
        lastName: "",
        email: "",
        additionalInfo: "",
      },
      loading: false,
      success: false,
      error: null,
      showStatusMessage: false,
      showConfirmation: false,
    };
  },
  computed: {
    isDateTimeSelected() {
      return this.selectedDateTime !== null && this.selectedDateTime.duration !== null && this.selectedDateTime.date !== null;
    },
    isIdentityInfoComplete() {
      return (
          this.identityInfo.firstName !== "" &&
          this.identityInfo.lastName !== "" &&
          this.identityInfo.email !== ""
      );
    },
    isTypeComplete() {
      return this.selectedType !== null;
    },
    statusMessage() {
      return this.success
          ? "Termin wurde erfolgreich vereinbart!"
          : this.error;
    },
  },
  async created() {
    await this.fetchWorkerData();
  },
  methods: {
    handleTypeUpdate(selectedType) {
      this.selectedType = selectedType;
    },
    async fetchWorkerData() {
      try {
        const workerData = await workerService.fetchWorker(
            this.$route.params.worker
        );
        const pfpResponse = await workerService.fetchProfilePicture(
            workerData.worker.id
        );
        this.worker = { ...workerData.worker, pfp: pfpResponse };
      } catch (error) {
        console.error("Error fetching worker or profile picture:", error);
        this.showStatusMessageWithError("Fehler beim Laden der Daten.");
      }
    },
    async submitAppointment() {
      if (!this.selectedDateTime.duration) {
        this.showStatusMessageWithError("Keine Termin Dauer ausgewählt.");
        return;
      }
      if (!this.selectedDateTime.date) {
        this.showStatusMessageWithError("Kein Datum und keine Uhrzeit ausgewählt.");
        return;
      }

      if (!this.isTypeComplete) {
        this.showStatusMessageWithError("Kein Meeting Typ ausgewählt.");
        return;
      }



      if (!validator.validate(this.identityInfo.email)) {
        this.showStatusMessageWithError("Angegebene email ist ungültig.");
        return;
      }

      this.loading = true;
      this.success = false;
      this.error = null;

      const { date, duration } = this.selectedDateTime;

      try {
        await requestService.requestAppointment({
          date,
          duration,
          worker_email: this.worker.email,
          identityInfo: this.identityInfo,
          type: this.selectedType,
        });

        this.success = true;
        this.showConfirmation = true;
        this.autoHideStatusMessage();
      } catch (error) {
        console.error("Error submitting appointment:", error);
        this.showStatusMessageWithError("Termin konnte nicht vereinbart werden.");
      } finally {
        this.loading = false;
      }
    },
    showStatusMessageWithError(message) {
      this.error = message;
      this.showStatusMessage = true;
      this.autoHideStatusMessage();
    },
    autoHideStatusMessage() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.showStatusMessage = false;
        }, 3000);
      });
    },
    handleDateTimeUpdate(dateTimeData) {
      this.selectedDateTime = dateTimeData;
    },
    handleIdentityUpdate(identityData) {
      this.identityInfo = identityData;
    },
  },
};
</script>



<style scoped>
.worker-name {
  text-align: center;
  font-weight: bold;
  font-size: 28px;
  margin-top: 15px;
}

.worker-role {
  text-align: center;
  font-size: large;
}

.multi-step-container {
  margin-bottom: 0;
}

.multi-step-container > * {
  margin-bottom: 0;
  max-width: 100% !important;
}

.submit-button-form > * > * {
  margin-right: 0 !important;
}

.button-container {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.next-button > * > * {
  margin-right: 0 !important;
}

.logo {
  display: block;
  margin: auto;
  height: 130px;
  border-radius: 10px;
}

.pre-form-container {
  height: 376px;

  margin: 20px 0 20px 0;
}



.container {
  background-color: #fff;
  box-sizing: border-box;
  padding: 1.5em;
  width: 38em;
  margin: 10px auto auto auto;
  max-width: calc(100% - 3em);
  border-radius: 0.5em;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}



.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999999999 !important;
}

/*.loading-spinner {
  border: 8px solid #f3f3f3;
  border-top: 8px solid #3498db;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1s linear infinite;
}*/

.loading-spinner {
  border-radius: 4px;
  width: 60px;
  height: 60px;
  animation: spin 1.5s linear infinite;
}


/*@keyframes smoothFadeIn {
  0% {
    opacity: 0;
    transform: scale(0.95);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

 */

@keyframes spin {
  0% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(180deg) scale(1.2);
  }
  100% {
    transform: rotate(360deg) scale(1);
  }
}

@keyframes smoothSlideInBottom {
  from {
    transform: translateY(25px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.slide-in-bottom {
  animation: smoothSlideInBottom 0.6s ease-out;
}


/*@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}*/

.status-message {
  padding: 1em;
  margin-top: 1em;
  border-radius: 0.5em;
}

.status-message.success {
  background-color: #d4edda;
  color: #155724;
}

.status-message.error {
  background-color: #f8d7da;
  color: #721c24;
}






.status-message {
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  max-width: 400px;
  background-color: #fff;
  border-bottom: 2px solid #ddd;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1em;
  text-align: center;
  transition: opacity 0.3s ease-in-out;
}

.status-message.success {
  background-color: #d4edda;
  color: #155724;
}

.status-message.error {
  background-color: #f8d7da;
  color: #721c24;
}


.slide-fade-enter, .slide-fade-leave-to /* .slide-fade-leave-active in <2.1.8 */ {
  opacity: 0;
}


.confirmation-message {
  text-align: center;
  padding: 1em;
  border: 1px solid #ddd;
  border-radius: 0.5em;
  background-color: #d4edda;
  color: #155724;
  margin-top: 1em;
}

@media (max-width: 600px) {
  .container{
    padding: 1em;


  }
  .container div{

    height: min-content;

  }


  .container{
    width: 100%;
    min-width: 100%;
    margin: 20px 0;
  }
}


</style>
